import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import Colors from '../../ui/global/Colors';
import Fonts from '../../ui/global/Fonts';
import Button from '../../ui/components/Button';
import ButtonMicro from '../../ui/components/ButtonMicro';
import MenuOpen from '../../ui/components/MenuOpen';
import { useUser } from '../../backend/useUser';
import { useCreditsContext, plans } from '../pricing/CreditsContext';
import Tooltip from '../../ui/components/Tooltip';
import { captureEvent } from '../../utils/analytics';
import { smartRound } from '../../utils/math';
import { useLocation } from 'react-router-dom';

const ProfileButtonRoot = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    z-index: 100;
`;

const RowContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: 6px;
`;

const SubText = styled.span<{ $colorMode: 'light' | 'dark' }>`
    color: ${props => props.$colorMode === 'dark' ? Colors.grey2 : Colors.grey8};
    font-weight: ${Fonts.lexendLight.fontWeight};
    font-size: 13px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

interface ProfileButtonProps {
    config: any;
    colorMode?: 'light' | 'dark';
}

const ProfileButton = ({ config, colorMode = 'dark' }: ProfileButtonProps) => {
    const location = useLocation();
    const profileButtonRef = useRef<HTMLDivElement>(null);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const [userPlan, setUserPlan] = useState<string | null>(null);
    const { user, onLogOut } = useUser();
    const { subscription, credits, isLoading: creditsLoading } = useCreditsContext();

    useEffect(() => {
        if (subscription) {
            setUserPlan(plans.find(p => p.key === subscription)?.name || null);
        }
    }, [subscription]);

    const handleClick = () => {
        if (profileButtonRef.current) {
            setAnchorEl(profileButtonRef.current);
            setMenuOpen(!menuOpen);
        }
    };

    const handleClose = () => {
        setMenuOpen(false);
        setAnchorEl(null);
    };

    const handleLogout = async () => {
        setMenuOpen(false);
        onLogOut(location.pathname);
        handleClose();
    };

    const handlePlanClick = (user) => {
        // console.log('Plan clicked', user?.email);
        const path = '/pricing';
        window.open(path, '_blank');
    };

    const handleDiscordClick = () => {
        window.open('https://discord.gg/ENQxsaBJqd', '_blank');
    };

    const creditsText = creditsLoading
        ? '...'
        : `${smartRound(credits.creditsUsed, 1)} / ${smartRound(credits.totalCredits, 0)} used`;

    return (
        <ProfileButtonRoot ref={profileButtonRef}>
            <Tooltip text={'Account'} colorMode="dark" hide={menuOpen}>
                <Button
                    size="tiny"
                    color={colorMode === 'dark' ? "ghost white" : "ghost"}
                    icon="person-fill"
                    onClick={handleClick}
                />
            </Tooltip>
            <MenuOpen
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleClose}
                options={[
                    { text: user?.email || '...' },
                    {
                        text: 'Plan',
                        text_right: userPlan || 'FREE',
                        disabled: !user,
                        onClick: () => handlePlanClick(user),
                        below: (
                            <RowContainer>
                                <SubText $colorMode={colorMode}>
                                    {userPlan === 'FREE' ? 'UPGRADE' : 'Monthly'}
                                </SubText>
                                <SubText $colorMode={colorMode}>
                                    {creditsText}
                                </SubText>
                            </RowContainer>
                        ),
                    },
                    // { text: '', text_sub: 'Join Discord Community', icon: 'discord', onClick: handleDiscordClick },
                    { text: 'Log out', icon: 'logout', onClick: handleLogout }
                ]}
                color={colorMode === 'dark' ? "black" : "white"}
                minWidth={180}
            />
        </ProfileButtonRoot>
    );
};

export default ProfileButton;